import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Business as ClienteIcon,
} from '@mui/icons-material';

const ClientesFacturacion = () => {
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', direccion: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClientes();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = clientes.filter(
        (c) =>
          c.nombre.toLowerCase().includes(search.toLowerCase()) ||
          (c.direccion && c.direccion.toLowerCase().includes(search.toLowerCase()))
      );
      setFilteredClientes(filtered);
    } else {
      setFilteredClientes(clientes);
    }
  }, [search, clientes]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const res = await api.get(endpoints.oficina.clientesfact);
      setClientes(res.data.data || []);
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingCliente(null);
    setFormData({ nombre: '', direccion: '' });
    setShowModal(true);
  };

  const handleOpenEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({ nombre: cliente.nombre, direccion: cliente.direccion || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      setError('El nombre es requerido');
      return;
    }

    try {
      setSaving(true);
      if (editingCliente) {
        await api.put(endpoints.oficina.clientesfact, {
          id: editingCliente.id,
          nombre: formData.nombre.trim(),
          direccion: formData.direccion.trim(),
        });
        setSuccess('Cliente actualizado correctamente');
      } else {
        await api.post(endpoints.oficina.clientesfact, {
          nombre: formData.nombre.trim(),
          direccion: formData.direccion.trim(),
        });
        setSuccess('Cliente agregado correctamente');
      }
      setShowModal(false);
      fetchClientes();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cliente) => {
    if (!confirm(`¿Eliminar el cliente "${cliente.nombre}"?`)) return;

    try {
      await api.delete(endpoints.oficina.clientesfact, { data: { id: cliente.id } });
      setSuccess('Cliente eliminado correctamente');
      fetchClientes();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Clientes de Facturación
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestión de clientes para facturación de vuelos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Agregar Cliente
          </Button>
          <Button component={Link} to="/facturacion" variant="contained" color="inherit" startIcon={<BackIcon />}>
            Volver
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            label="Buscar cliente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            placeholder="Buscar por nombre o dirección..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Dirección</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClientes.map((cliente) => (
                <TableRow key={cliente.id} hover>
                  <TableCell>{cliente.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ClienteIcon color="primary" fontSize="small" />
                      <Typography fontWeight="bold">{cliente.nombre}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{cliente.direccion || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(cliente)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => handleDelete(cliente)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredClientes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay clientes</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Modal */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingCliente ? 'Editar Cliente' : 'Agregar Cliente'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  fullWidth
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Dirección"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Guardar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ClientesFacturacion;




